import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Clock, MapPin, Loader2, LogIn, LogOut, CheckCircle2, AlertTriangle,
  CalendarOff, RefreshCw, History, ChevronLeft, ChevronRight, Lock,
} from 'lucide-react'
import { apiGet, postJson, cachedGet } from '../../lib/api.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import {
  clockinTs, clockoutTs, shiftStart, shiftEnd, lateMinutes, otValue, travelFee,
  workedMinutes, fmtDuration, fmtTime, fmtDate, rowStatus, isLeaveRow,
  relabelBranch, summarize, payrollPeriod, shiftPeriod, otMode,
} from '../../lib/clockin.js'
import {
  bkkToday, bkkYesterday, getPosition, isFixFresh, distanceMeters, fmtMeters,
  branchPoint, readableError, POOR_ACCURACY_M, CLOCKIN_RADIUS_M,
} from '../../lib/meClockin.js'

// พื้นที่ของฉัน → ลงเวลา. The staff member punches their OWN shift.
//
// A punch attaches to a SCHEDULED SHIFT (an employee_timetable row) — it does
// not create one. So the page is built around "today's shift", not around a
// bare clock: with no shift on the roster there is nothing to punch, and saying
// that plainly is more useful than a button that will always be refused.
//
// Everything that could be lied about is decided on the server
// (functions/api/me/clockin/[id].js): the timestamp, the branch the distance is
// measured from, whose row it is, and that a punch cannot be overwritten. This
// page shapes the interaction; it does not enforce it.
//
// The one thing it does add is a PRE-FLIGHT. The live Bubble logs show ~4 in 10
// punches rejected for distance — and reading the distances back, they cluster at
// 1.0–1.2 km from a 1 km fence, from people who were standing in the clinic the
// whole time and got through seconds later without moving. That is GPS drift, not
// cheating. So the page waits for a fix, says whether it is usable, and disables
// the button only when the fix is out of range even at its most generous reading.

export default function MeClockin() {
  const [rows, setRows] = useState([])          // yesterday + today
  const [branches, setBranches] = useState({})  // id → branch row
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [okMsg, setOkMsg] = useState('')

  const [fix, setFix] = useState(null)
  const [fixErr, setFixErr] = useState('')
  const [locating, setLocating] = useState(false)

  const [busyId, setBusyId] = useState(0)
  const [confirmOut, setConfirmOut] = useState(null) // the row awaiting confirmation
  // Bumped on every successful punch. HistorySection fetches its own range, so
  // without this it only ever refreshes when the period changes — and today's
  // shift is IN that range, so it sat one action stale after every punch.
  const [punchSeq, setPunchSeq] = useState(0)

  // Ticks once a second so the header clock moves and "is this fix still fresh"
  // is re-evaluated rather than frozen at mount.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  // The success banner clears itself. It confirms an action the user just took
  // and the punch box beside it already shows the time permanently — leaving it
  // on screen turns a confirmation into a notice that has to be dismissed.
  // Errors do NOT auto-clear: those need reading and acting on.
  useEffect(() => {
    if (!okMsg) return
    const t = setTimeout(() => setOkMsg(''), 6000)
    return () => clearTimeout(t)
  }, [okMsg])

  // ok / locating are states with nothing to do about them → a small pill in the
  // header. poor / error need words and an action → a full-width banner. The
  // good state gets out of the way; the bad state takes the space.
  const gpsState = fixErr ? 'error'
    : fix && fix.accuracy > POOR_ACCURACY_M ? 'poor'
      : fix ? 'ok' : 'locating'

  // Strings, not `now` — they change once a day, so everything keyed off them
  // stays stable across the one-second tick.
  const today = bkkToday(now)
  const yesterday = bkkYesterday(now)

  const load = useCallback(async () => {
    setErr('')
    try {
      const r = await apiGet('/api/me/clockin', {
        start_date: bkkYesterday(), end_date: bkkToday(), per_page: 20,
      })
      setRows(Array.isArray(r?.items) ? r.items : [])
    } catch (e) {
      setErr(readableError(e, 'โหลดตารางเวรไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    cachedGet('/api/branches')
      .then((r) => {
        const list = Array.isArray(r) ? r : (r?.items || [])
        setBranches(Object.fromEntries(list.map((b) => [String(b.id), b])))
      })
      .catch(() => { /* the distance hint is optional — the server still gates */ })
  }, [])

  const locate = useCallback(async () => {
    setLocating(true)
    setFixErr('')
    try {
      const p = await getPosition()
      setFix(p)
      return p
    } catch (e) {
      setFixErr(e?.message || 'หาตำแหน่งไม่สำเร็จ')
      return null
    } finally {
      setLocating(false)
    }
  }, [])

  useEffect(() => { locate() }, [locate])

  /* Which rows can be acted on today.
   * - today's shifts, always
   * - yesterday's shift ONLY while its clock-out is still open, so a shift that
   *   ran past midnight can be closed. The proxy allows exactly this pair; a
   *   yesterday row that is already closed belongs to the history list. */
  const actionable = useMemo(() => rows.filter((r) => {
    const day = String(r.date || '').slice(0, 10)
    if (isLeaveRow(r)) return day === today       // shown, but as "no punch needed"
    if (day === today) return true
    return day === yesterday && clockinTs(r) != null && clockoutTs(r) == null
  }), [rows, today, yesterday])

  async function punch(row, action) {
    setBusyId(row.id)
    setErr('')
    setOkMsg('')
    try {
      // Always take a fresh reading at the moment of the punch — a fix acquired
      // when the page opened is not evidence of where the person is standing
      // now. Fall back to the existing one only if it is still fresh.
      let p = await locate()
      if (!p && isFixFresh(fix, Date.now())) p = fix
      if (!p) throw new Error(fixErr || 'ยังไม่ได้ตำแหน่ง GPS')

      const res = await postJson(`/api/me/clockin/${row.id}`, {
        action, lat: p.lat, lon: p.lon, accuracy: p.accuracy,
      })
      setRows((list) => list.map((r) => (r.id === row.id ? (res.row || r) : r)))
      setPunchSeq((n) => n + 1)
      setOkMsg(action === 'in'
        ? `ลงเวลาเข้างานแล้ว ${fmtTime(res.at)} น.`
        : `ลงเวลาออกงานแล้ว ${fmtTime(res.at)} น.`)
    } catch (e) {
      setErr(readableError(e))
      // A 409 means the server's view and ours have diverged (another device
      // punched, most likely) — re-read rather than leaving a stale button.
      if (e?.status === 409) load()
    } finally {
      setBusyId(0)
    }
  }

  return (
    <div className="max-w-[720px]">
      <NowHeader
        now={now}
        gps={<GpsPill state={gpsState} locating={locating} onRefresh={locate} />}
      />

      {okMsg && (
        <Banner tone="ok" icon={CheckCircle2} onClose={() => setOkMsg('')}>{okMsg}</Banner>
      )}
      {err && (
        <Banner tone="bad" icon={AlertTriangle} onClose={() => setErr('')}>{err}</Banner>
      )}

      <GpsBanner state={gpsState} message={fixErr} locating={locating} onRefresh={locate} />

      {loading && (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-400 py-10 justify-center">
          <Loader2 size={15} className="animate-spin" /> กำลังโหลด…
        </div>
      )}

      {!loading && !actionable.length && <NoShift />}

      {!loading && actionable.map((row) => (
        <ShiftCard
          key={row.id}
          row={row}
          today={today}
          branch={branches[String(row.branch_id)]}
          fix={fix}
          busy={busyId === row.id}
          anyBusy={busyId !== 0}
          onIn={() => punch(row, 'in')}
          onOut={() => setConfirmOut(row)}
        />
      ))}

      <HistorySection reloadKey={punchSeq} branches={branches} />

      {confirmOut && (
        <ConfirmDialog
          title="ลงเวลาออกงาน?"
          message={`บันทึกเวลาออกงานเป็น ${fmtTime(now)} น. — แก้ไขเองภายหลังไม่ได้ หากผิดต้องให้ฝ่ายบุคคลเป็นผู้แก้`}
          confirmLabel="ออกงาน"
          onConfirm={() => { const r = confirmOut; setConfirmOut(null); punch(r, 'out') }}
          onCancel={() => setConfirmOut(null)}
        />
      )}
    </div>
  )
}

/* ── header ──────────────────────────────────────────────────────────────── */

const DAYS_TH = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']

function NowHeader({ now, gps }) {
  const d = new Date(now)
  const pad = (n) => String(n).padStart(2, '0')
  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <div className="text-[34px] font-semibold text-slate-900 tabular-nums leading-none tracking-tight">
          {pad(d.getHours())}:{pad(d.getMinutes())}
          <span className="text-[20px] text-slate-400">:{pad(d.getSeconds())}</span>
        </div>
        <div className="text-[13px] text-slate-500 mt-1.5">
          วัน{DAYS_TH[d.getDay()]}ที่ {fmtDate(now)}
        </div>
      </div>
      <div className="flex-none pt-1">{gps}</div>
    </div>
  )
}

/* ── GPS ─────────────────────────────────────────────────────────────────── */

// No numbers in any state. The accuracy radius is how this decides what to say
// — it is not itself worth saying. "±35 ม." is a confidence interval; the staff
// member's only question is "can I press the button yet", and a number they have
// to interpret answers it worse than a colour and a sentence do. Staleness is
// silent for the same reason: the punch re-acquires anyway.

// ok / locating — nothing for the user to do, so it shrinks to a pill beside the
// clock and stops competing with the punch button for attention.
function GpsPill({ state, locating, onRefresh }) {
  if (state === 'poor' || state === 'error') return null
  const ready = state === 'ok'
  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={locating}
      title="หาตำแหน่งใหม่"
      className={`inline-flex items-center gap-1.5 rounded-full ring-1 pl-2.5 pr-2 py-1 text-[11.5px] transition disabled:opacity-60 ${
        ready
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100'
          : 'bg-slate-50 text-slate-500 ring-slate-200/70'
      }`}
    >
      {locating
        ? <Loader2 size={11.5} className="animate-spin" />
        : <MapPin size={11.5} strokeWidth={2} />}
      <span>{ready ? 'พร้อมลงเวลา' : 'กำลังหาตำแหน่ง…'}</span>
      {!locating && <RefreshCw size={10.5} strokeWidth={2} className="opacity-50" />}
    </button>
  )
}

// poor / error — these need words and an action, so they take the full width.
function GpsBanner({ state, message, locating, onRefresh }) {
  if (state !== 'poor' && state !== 'error') return null
  const bad = state === 'error'
  const text = bad
    ? (message || 'หาตำแหน่งไม่สำเร็จ')
    : 'กำลังหาตำแหน่งให้แม่นยำขึ้น — รอสักครู่ หรือออกไปที่โล่ง'
  return (
    <div className={`flex items-start gap-2 rounded-xl ring-1 px-3.5 py-2.5 mb-4 text-[12.5px] ${
      bad ? 'bg-rose-50 text-rose-700 ring-rose-200' : 'bg-amber-50 text-amber-700 ring-amber-200'
    }`}>
      {locating
        ? <Loader2 size={14} className="mt-[2px] flex-none animate-spin" />
        : <MapPin size={14} className="mt-[2px] flex-none" strokeWidth={1.9} />}
      <span className="min-w-0">{text}</span>
      <button
        type="button"
        onClick={onRefresh}
        disabled={locating}
        title="หาตำแหน่งใหม่"
        className="ml-auto flex-none inline-flex items-center gap-1 opacity-70 hover:opacity-100 disabled:opacity-40"
      >
        <RefreshCw size={12.5} strokeWidth={2} /> รีเฟรช
      </button>
    </div>
  )
}

/* ── today's shift ───────────────────────────────────────────────────────── */

function NoShift() {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-10 text-center">
      <div className="w-11 h-11 rounded-xl bg-white text-slate-400 flex items-center justify-center mx-auto mb-3 ring-1 ring-slate-200/70">
        <CalendarOff size={20} strokeWidth={1.75} />
      </div>
      <div className="text-[15px] font-semibold text-slate-800">วันนี้ไม่มีเวรในตาราง</div>
      <p className="text-[13px] text-slate-500 leading-relaxed mt-2 max-w-[420px] mx-auto">
        การลงเวลาจะผูกกับ<strong className="font-medium text-slate-600">เวรที่จัดไว้ในตารางเวร</strong> —
        ถ้าวันนี้ควรมีเวร กรุณาแจ้งหัวหน้าหรือฝ่ายบุคคลให้เพิ่มเวรก่อน แล้วจึงลงเวลาได้
      </p>
    </div>
  )
}

function ShiftCard({ row, today, branch, fix, busy, anyBusy, onIn, onOut }) {
  const inTs = clockinTs(row)
  const outTs = clockoutTs(row)
  const leave = isLeaveRow(row)
  const day = String(row.date || '').slice(0, 10)
  const yesterdayRow = day !== today
  const st = rowStatus(row)

  const point = branchPoint(branch)
  const dist = fix && point ? distanceMeters(fix, point) : null

  /* Out of range = out of range EVEN AT THE FIX'S MOST GENEROUS READING.
   * A punch at 1,050 m on a ±300 m fix could really be at 750 m — and the live
   * logs show exactly that: a cluster of rejections at 1.0–1.2 km from อารีย์
   * from people who were standing in the clinic the whole time, whose fix simply
   * had not converged yet. Blocking on the raw distance would turn that drift
   * into a dead button with no way forward.
   * With no fix and no branch point there is no distance, so nothing is blocked
   * — Xano still decides. This never blocks on missing data, only on evidence. */
  const acc = Number(fix?.accuracy) || 0

  /* WFH is exempt, because being away from the branch is the point of it.
   * api 573 skips the geofence for this type, so gating the button here would
   * disable a punch the server would happily accept — which is exactly the
   * state WFH was in before: 250 rostered days, zero punches, every one of them
   * counted as ขาดลงเวลา. The distance is still measured and still shown for
   * every other type; the server remains the authority for all of them. */
  const geofenced = row.type !== 'WFH'
  const tooFar = geofenced && dist != null && (dist - acc) > CLOCKIN_RADIUS_M

  const done = inTs != null && outTs != null
  const stage = leave ? 'leave' : inTs == null ? 'in' : outTs == null ? 'out' : 'done'

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white mb-4 overflow-hidden">
      <header className="flex items-center gap-2 px-4 sm:px-5 py-3.5 border-b border-slate-100">
        <Clock size={15} className="text-brand-600 flex-none" strokeWidth={1.9} />
        <h2 className="text-[14px] font-semibold text-slate-800">{row.type || 'เวร'}</h2>
        <span className="text-[12px] text-slate-400 truncate">
          {relabelBranch(branch?.name)}
          {shiftStart(row) ? ` · ${fmtTime(shiftStart(row))}–${fmtTime(shiftEnd(row))}` : ''}
        </span>
        <span className={`ml-auto flex-none text-[11.5px] px-2 py-[3px] rounded-full ring-1 ${st.tone}`}>
          {st.label}
        </span>
      </header>

      <div className="px-4 sm:px-5 py-4">
        {yesterdayRow && (
          <p className="flex items-start gap-1.5 text-[12px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-xl px-3 py-2 mb-3.5">
            <AlertTriangle size={13} className="mt-[2px] flex-none" strokeWidth={1.9} />
            <span>นี่คือเวรของวันที่ {fmtDate(row.date)} ที่ยังไม่ได้ลงเวลาออกงาน</span>
          </p>
        )}

        {/* The two punches, side by side — the state of the day in one glance. */}
        <div className="grid grid-cols-2 gap-2.5 mb-4">
          <PunchBox label="เข้างาน" ts={inTs} note={lateMinutes(row) > 0 ? `สาย ${lateMinutes(row)} นาที` : ''} noteTone="text-rose-600" />
          <PunchBox label="ออกงาน" ts={outTs} note={done ? fmtDuration(workedMinutes(row)) : ''} noteTone="text-slate-400" />
        </div>

        {stage === 'leave' && (
          <p className="text-[13px] text-slate-500">วันนี้เป็นวัน{row.type} — ไม่ต้องลงเวลา</p>
        )}

        {(stage === 'in' || stage === 'out') && (
          <>
            {/* Distance is shown ONLY when it is a problem. In range it is
                reassurance the user did not ask for; out of range it is the one
                thing that explains the refusal before they tap. */}
            {tooFar && (
              <p className="flex items-start gap-1.5 text-[12.5px] text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-xl px-3 py-2 mb-3">
                <MapPin size={13} className="mt-[2px] flex-none" strokeWidth={1.9} />
                <span>
                  คุณอยู่ห่างจากสาขา{relabelBranch(branch?.name)}ประมาณ{' '}
                  <strong className="font-medium tabular-nums">{fmtMeters(dist)}</strong> —
                  ต้องอยู่ที่คลินิกจึงจะลงเวลาได้ ปุ่มจะเปิดให้กดเมื่ออยู่ในระยะ
                </span>
              </p>
            )}

            <button
              type="button"
              onClick={stage === 'in' ? onIn : onOut}
              disabled={anyBusy || tooFar}
              className={`w-full flex items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-[15px] font-semibold text-white transition disabled:opacity-40 disabled:cursor-not-allowed ${
                stage === 'in'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-slate-700 hover:bg-slate-800'
              }`}
            >
              {busy
                ? <><Loader2 size={17} className="animate-spin" /> กำลังบันทึก…</>
                : stage === 'in'
                  ? <><LogIn size={17} strokeWidth={2} /> ลงเวลาเข้างาน</>
                  : <><LogOut size={17} strokeWidth={2} /> ลงเวลาออกงาน</>}
            </button>

            <p className="flex items-start gap-1.5 text-[11.5px] text-slate-400 mt-3">
              <Lock size={11.5} className="mt-[2px] flex-none" strokeWidth={1.9} />
              <span>
                เวลาที่บันทึกใช้เวลาของระบบและตำแหน่ง GPS ณ ตอนที่กด — แก้ไขเองภายหลังไม่ได้
                หากผิดต้องให้ฝ่ายบุคคลเป็นผู้แก้
              </span>
            </p>
          </>
        )}

        {stage === 'done' && (
          <p className="flex items-center gap-1.5 text-[13px] text-emerald-700">
            <CheckCircle2 size={14} strokeWidth={1.9} />
            ลงเวลาครบแล้วสำหรับเวรนี้
            {otValue(row) !== 0 && (
              <span className="text-slate-400">· OT/ออกก่อน {otValue(row)}</span>
            )}
          </p>
        )}
      </div>
    </section>
  )
}

function PunchBox({ label, ts, note, noteTone }) {
  return (
    <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200/70 px-3.5 py-2.5">
      <div className="text-[11.5px] text-slate-400">{label}</div>
      <div className={`text-[20px] font-semibold tabular-nums leading-tight ${ts ? 'text-slate-900' : 'text-slate-300'}`}>
        {ts ? fmtTime(ts) : '--:--'}
      </div>
      {note && <div className={`text-[11.5px] mt-0.5 ${noteTone}`}>{note}</div>}
    </div>
  )
}

/* ── history ─────────────────────────────────────────────────────────────
 * Scoped to the PAYROLL period (26th → 25th), not to a calendar month, because
 * the question this list answers is "what will my next payslip be built from".
 * Same window the HR report opens on. */

function HistorySection({ reloadKey = 0, branches = {} }) {
  const [period, setPeriod] = useState(() => payrollPeriod())
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const reqRef = useRef(0)

  useEffect(() => {
    const seq = ++reqRef.current
    setLoading(true)
    setErr('')
    apiGet('/api/me/clockin', { start_date: period.start, end_date: period.end, per_page: 60 })
      .then((r) => {
        if (seq !== reqRef.current) return
        setRows(Array.isArray(r?.items) ? r.items : [])
        setTotal(Number(r?.itemsTotal) || 0)
      })
      .catch((e) => { if (seq === reqRef.current) setErr(readableError(e, 'โหลดประวัติไม่สำเร็จ')) })
      .finally(() => { if (seq === reqRef.current) setLoading(false) })
  }, [period.start, period.end, reloadKey])

  const sorted = useMemo(
    () => [...rows].sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [rows],
  )
  const sum = useMemo(() => summarize(rows), [rows])
  const atCurrent = period.end === payrollPeriod().end

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white mt-6">
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-4 sm:px-5 py-3.5 border-b border-slate-100">
        <History size={15} className="text-brand-600 flex-none" strokeWidth={1.9} />
        <h2 className="text-[14px] font-semibold text-slate-800 whitespace-nowrap">ประวัติการลงเวลา</h2>
        <span className="text-[11.5px] text-slate-400 whitespace-nowrap">รอบเงินเดือน</span>
        <div className="ml-auto flex items-center gap-1 flex-none">
          <StepBtn onClick={() => setPeriod((p) => shiftPeriod(p, -1))} title="รอบก่อนหน้า">
            <ChevronLeft size={15} strokeWidth={2} />
          </StepBtn>
          <span className="text-[12px] text-slate-500 tabular-nums px-1 whitespace-nowrap">
            {fmtDate(period.start)} – {fmtDate(period.end)}
          </span>
          <StepBtn onClick={() => setPeriod((p) => shiftPeriod(p, 1))} title="รอบถัดไป" disabled={atCurrent}>
            <ChevronRight size={15} strokeWidth={2} />
          </StepBtn>
        </div>
      </header>

      {/* Period totals. สาย/OT are Xano's own numbers — shown as-is, never
          restated in a unit this page decided on. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-slate-100 border-b border-slate-100">
        <Stat label="เวร" value={sum.shifts} />
        <Stat label="ขาดลงเวลา" value={sum.missing} tone={sum.missing ? 'text-amber-600' : ''} />
        <Stat label="สาย (ครั้ง)" value={sum.lateCount} tone={sum.lateCount ? 'text-rose-600' : ''} />
        <Stat label="ลา" value={sum.leave} />
      </div>

      <div className="px-4 sm:px-5 py-3">
        {loading && (
          <div className="flex items-center gap-2 text-[13px] text-slate-400 py-6 justify-center">
            <Loader2 size={14} className="animate-spin" /> กำลังโหลด…
          </div>
        )}
        {!loading && err && <p className="text-[13px] text-rose-500 py-4">{err}</p>}
        {!loading && !err && !sorted.length && (
          <p className="text-[13px] text-slate-400 py-6 text-center">ไม่มีเวรในรอบนี้</p>
        )}
        {/* A real table with AUTO layout, so each column takes only the width its
            content needs — "กะบ่าย" wants 44px, not the 78px a fixed grid reserved
            for it. That is what removed the horizontal scroll; nothing was cut.
            Below `sm` the two least load-bearing columns drop out: สถานะ is
            derivable from the times already shown, and เดินทาง is 0 on most rows. */}
        {!loading && !err && !!sorted.length && (
          <div className="sm:hidden">
            {sorted.map((r) => (
              <HistoryCard key={r.id} row={r} branch={branches[String(r.branch_id)]} />
            ))}
          </div>
        )}
        {!loading && !err && !!sorted.length && (
          <table className="hidden sm:table w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="text-[11px] font-medium text-slate-400 border-b border-slate-200/70">
                <Th>วันที่</Th>
                <Th>ประเภท</Th>
                <Th>สาขา</Th>
                <Th>เข้า – ออก</Th>
                <Th right>สาย</Th>
                <Th>OT</Th>
                <Th right>เดินทาง</Th>
                <Th right>สถานะ</Th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <HistoryRow key={r.id} row={r} branch={branches[String(r.branch_id)]} />
              ))}
            </tbody>
          </table>
        )}
        {/* One page holds a whole period in practice. Say so out loud if it
            ever doesn't — a truncated list reads as "these are all my shifts". */}
        {!loading && !err && total > sorted.length && (
          <p className="text-[11.5px] text-amber-600 pt-3">
            แสดง {sorted.length} จาก {total} รายการในรอบนี้ — ดูครบได้ที่ฝ่ายบุคคล
          </p>
        )}
      </div>
    </section>
  )
}

function StepBtn({ children, ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className="w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50 flex items-center justify-center disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  )
}

function Stat({ label, value, tone = '' }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[11.5px] text-slate-400 whitespace-nowrap">{label}</div>
      <div className={`text-[17px] font-semibold tabular-nums ${tone || 'text-slate-800'}`}>{value}</div>
    </div>
  )
}

const CELL = 'px-2 py-2.5 first:pl-0 last:pr-0 align-middle'

function Th({ children, right }) {
  return (
    <th className={`font-medium pb-2 px-2 first:pl-0 last:pr-0 ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  )
}

// อนุมัติ OT vs OT พิเศษ are mutually exclusive by Xano's own rule, so this is
// one badge, never two. See otMode() in lib/clockin.js. The labels say "OT"
// in full even though the column header repeats it: "อนุมัติ" on its own does
// not say WHAT was approved, and this number reaches payroll.
const OT_META = {
  allow:   { label: 'อนุมัติ OT', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  special: { label: 'OT พิเศษ',   tone: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
}

function HistoryRow({ row, branch }) {
  const st = rowStatus(row)
  const late = lateMinutes(row)
  const ot = otValue(row)
  const mode = otMode(row)
  const fee = travelFee(row)
  const leave = isLeaveRow(row)
  // relabelBranch is not cosmetic here: branch 1 and branch 8 would otherwise
  // both read "อารีย์" and the column would be a lie.
  const bname = relabelBranch(branch?.name)

  return (
    <tr className="border-b border-slate-50 last:border-0">
      <td className={`${CELL} text-slate-700 tabular-nums whitespace-nowrap`}>{fmtDate(row.date)}</td>
      <td className={`${CELL} text-slate-600 whitespace-nowrap`}>{row.type || '—'}</td>
      <td className={`${CELL} text-slate-500`}>
        <span className="block max-w-[120px] truncate" title={bname}>{bname}</span>
      </td>
      <td className={`${CELL} text-slate-600 tabular-nums whitespace-nowrap`}>
        {leave
          ? <span className="text-slate-300">—</span>
          : `${clockinTs(row) ? fmtTime(clockinTs(row)) : '--:--'} – ${clockoutTs(row) ? fmtTime(clockoutTs(row)) : '--:--'}`}
      </td>
      <td className={`${CELL} text-right tabular-nums whitespace-nowrap ${late > 0 ? 'text-rose-600' : 'text-slate-300'}`}>
        {late > 0 ? `${late} น.` : '—'}
      </td>
      {/* The OT number is Xano's — baht for a real OT block, minutes inside the
          ≤1h grace band. Sign-coloured, no unit: this page does not get to
          decide which one it is. */}
      <td className={CELL}>
        <span className="flex items-center gap-1.5">
          {mode !== 'none' && (
            <span className={`text-[10.5px] px-1.5 py-[1px] rounded-full ring-1 whitespace-nowrap ${OT_META[mode].tone}`}>
              {OT_META[mode].label}
            </span>
          )}
          {ot !== 0 && (
            <span className={`tabular-nums ${ot > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{ot}</span>
          )}
          {mode === 'none' && ot === 0 && <span className="text-slate-300">—</span>}
        </span>
      </td>
      <td className={`${CELL} text-right tabular-nums ${fee > 0 ? 'text-slate-600' : 'text-slate-300'}`}>
        {fee > 0 ? fee : '—'}
      </td>
      <td className={`${CELL} text-right`}>
        <span className={`text-[11px] px-2 py-[2px] rounded-full ring-1 whitespace-nowrap ${st.tone}`}>{st.label}</span>
      </td>
    </tr>
  )
}

/* ── banners ─────────────────────────────────────────────────────────────── */

// Below `sm` the eight columns become a stacked block. Same eight facts, none
// dropped — read top-to-bottom instead of left-to-right. A table that scrolls
// sideways on a phone is a table whose right half nobody ever reads, and this
// area is used almost entirely on phones.
function HistoryCard({ row, branch }) {
  const st = rowStatus(row)
  const late = lateMinutes(row)
  const ot = otValue(row)
  const mode = otMode(row)
  const fee = travelFee(row)
  const leave = isLeaveRow(row)
  const bname = relabelBranch(branch?.name)

  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-medium text-slate-800 tabular-nums flex-none">{fmtDate(row.date)}</span>
        <span className="text-[12px] text-slate-400 truncate">{row.type} · {bname}</span>
        <span className={`ml-auto flex-none text-[11px] px-2 py-[2px] rounded-full ring-1 whitespace-nowrap ${st.tone}`}>
          {st.label}
        </span>
      </div>
      {/* Wraps rather than truncating: every item here is a payroll figure, so
          the row grows a line instead of hiding one. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[12.5px]">
        <span className="text-slate-600 tabular-nums">
          {leave
            ? '—'
            : `${clockinTs(row) ? fmtTime(clockinTs(row)) : '--:--'} – ${clockoutTs(row) ? fmtTime(clockoutTs(row)) : '--:--'}`}
        </span>
        {late > 0 && <span className="text-rose-600 tabular-nums">สาย {late} น.</span>}
        {/* The badge carries "OT" here — unlike the table, there is no column
            header above it to supply the word. */}
        {mode !== 'none' && (
          <span className={`text-[10.5px] px-1.5 py-[1px] rounded-full ring-1 whitespace-nowrap ${OT_META[mode].tone}`}>
            {OT_META[mode].label}
          </span>
        )}
        {ot !== 0 && (
          <span className={`tabular-nums ${ot > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>OT {ot}</span>
        )}
        {fee > 0 && <span className="text-slate-500 tabular-nums">เดินทาง {fee}</span>}
      </div>
    </div>
  )
}

function Banner({ tone, icon: Icon, onClose, children }) {
  const cls = tone === 'ok'
    ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
    : 'bg-rose-50 text-rose-700 ring-rose-200'
  return (
    <div className={`flex items-start gap-2 rounded-xl ring-1 px-3.5 py-2.5 mb-4 text-[13px] ${cls}`}>
      <Icon size={14} className="mt-[2px] flex-none" strokeWidth={1.9} />
      <span className="min-w-0">{children}</span>
      <button type="button" onClick={onClose} className="ml-auto flex-none opacity-50 hover:opacity-100 text-[16px] leading-none">×</button>
    </div>
  )
}
